import { Link, router, usePage } from "@inertiajs/react";
import { DropdownMenu, Heading, IconButton } from "@radix-ui/themes";
import classNames from "classnames";
import { Menu, Monitor, Moon, Sun } from "lucide-react";
import { useState } from "react";
import type { SharedBrand, SharedProps } from "@/types";
import { signOut } from "@/shared/nav/signOut";
import {
  currentAppearance,
  setAppearance,
  type Appearance,
} from "@/shared/appearance";

const APPEARANCE_ICONS = { system: Monitor, light: Sun, dark: Moon };

// The three places anybody, signed in or not, comes to Driftables for.
// Everything else a signed-in user can do lives in the sidebar.
const PRIMARY = [
  { label: "Tracks", href: "/tracks" },
  { label: "Events", href: "/events" },
  { label: "Clubs", href: "/clubs" },
];

// A branded site has no sidebar, so its top bar is all of it: the brand's
// events — its front page — and, signed in, your applications. Profile and
// settings are in the account menu. An admin's preview on driftables.net has
// no branded front page, so its Events is the events page.
const brandedLinks = (brand: SharedBrand, signedIn: boolean) => [
  { label: "Events", href: brand.preview ? "/events" : "/" },
  ...(signedIn ? [{ label: "Your applications", href: "/applications" }] : []),
];

const linkClass =
  "select-none rounded px-2 py-2 text-[15px] font-medium leading-none no-underline outline-none transition-colors hover:bg-topbar-content/10 focus-visible:shadow-[0_0_0_2px] focus-visible:shadow-accent7 sm:px-3";

interface Props {
  /** Opens the sidebar drawer below md. Only passed when there's a sidebar. */
  onOpenMenu?: () => void;
}

export default function TopBar({ onOpenMenu }: Props) {
  const { props, url } = usePage<SharedProps>();
  const path = url.split("?")[0];
  // Send the user back to the page they were on after signing in/up.
  // Both Devise controllers store this via store_location_for (see Users::SessionsController).
  const returnTo = encodeURIComponent(url);
  const user = props.currentUser;
  const links = props.brand ? brandedLinks(props.brand, Boolean(user)) : PRIMARY;

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-1 bg-topbar px-2 text-topbar-content sm:gap-4 sm:px-4">
      {/* md:hidden on a wrapper: on the IconButton itself, Radix's unlayered
          `display: inline-flex` beats it (see AppSidebar). */}
      {onOpenMenu && (
        <div className="flex md:hidden">
          <IconButton
            variant="ghost"
            color="gray"
            size="3"
            aria-label="Open menu"
            onClick={onOpenMenu}
          >
            <Menu size={20} className="text-topbar-content" />
          </IconButton>
        </div>
      )}

      <BrandMark brand={props.brand} />

      <nav aria-label="Primary" className="flex items-center gap-0.5 sm:gap-1">
        {links.map((item) => {
          const active = path === item.href || path.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={classNames(
                linkClass,
                active ? "text-accent9" : "text-topbar-content",
              )}
              aria-current={active ? "page" : undefined}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="ml-auto flex items-center gap-3">
        <AppearanceMenu />
        {user ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton
                radius="full"
                variant="soft"
                aria-label="Account menu"
              >
                {user.email.charAt(0).toUpperCase()}
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Label>{user.email}</DropdownMenu.Label>
              <DropdownMenu.Item onSelect={() => router.visit("/profile")}>
                Profile
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={() => router.visit("/settings")}>
                Settings
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onSelect={signOut}>Sign out</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : (
          // A Devise route, so a plain anchor. One door: signing in and
          // signing up are the same email link.
          <a
            className={classNames(linkClass, "text-accent9")}
            href={`/users/sign_in?return_to=${returnTo}`}
          >
            Sign In
          </a>
        )}
      </div>
    </header>
  );
}

// The home link: the brand's logo (or name), or DRIFTABLES. Mirrors
// layouts/_brand_mark.html.erb.
function BrandMark({ brand }: { brand: SharedProps["brand"] }) {
  if (!brand) {
    return (
      <Link href="/" className="hidden no-underline sm:block md:w-[13.5rem]" aria-label="Driftables home">
        <Heading size="3" className="text-accent9">
          DRIFTABLES
        </Heading>
      </Link>
    );
  }

  return (
    <Link href="/" className="hidden items-center no-underline sm:flex md:w-[13.5rem]" aria-label={`${brand.name} home`}>
      {brand.logo_url ? (
        <img src={brand.logo_url} alt={brand.name} className="h-8 w-auto max-w-[12rem] object-contain" />
      ) : (
        <Heading size="3" className="text-topbar-content">
          {brand.name}
        </Heading>
      )}
    </Link>
  );
}

function AppearanceMenu() {
  const [appearance, setChoice] = useState<Appearance>(currentAppearance);
  const Icon = APPEARANCE_ICONS[appearance];

  const choose = (value: string) => {
    setAppearance(value as Appearance);
    setChoice(value as Appearance);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton variant="ghost" color="gray" size="3" aria-label="Appearance">
          <Icon size={18} className="text-topbar-content" />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <DropdownMenu.RadioGroup value={appearance} onValueChange={choose}>
          <DropdownMenu.RadioItem value="system">System</DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="light">Light</DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="dark">Dark</DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
